import { STORAGE_TOKENS } from "@/shared/lib/storageTokens";
import {
  UserRole,
  Permission,
  hasPermission as checkPermission,
  hasAnyPermission as checkAnyPermission,
} from "@/shared/types/roles";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { useLogout } from "@/features/auth/hooks/auth.hooks";
import { AuthResponse } from ".";
import * as Sentry from "@sentry/react";

type User = {
  id: string;
  email: string;
  role: UserRole;
  avatar?: string;
  name?: string;
  username?: string;
};

type AuthContextType = {
  user: User | null;
  login: (response: AuthResponse) => void;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const logoutMutation = useLogout();

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem(STORAGE_TOKENS.USER_DATA);
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser) as User;
        setUser(parsedUser);
        Sentry.setUser({ id: parsedUser.id, email: parsedUser.email });
        Sentry.setTag("user_role", parsedUser.role);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const login = (response: AuthResponse) => {
    const userData: User = {
      id: response.userId,
      email: response.email,
      role: response.role as UserRole,
      avatar: response.profile.avatar,
      name: response.profile.name,
      username: response.profile.username,
    };

    setUser(userData);
    Sentry.setUser({ id: userData.id, email: userData.email });
    Sentry.setTag("user_role", userData.role);

    // Persist user info locally to re-hydrate context on reload
    localStorage.setItem(STORAGE_TOKENS.USER_DATA, JSON.stringify(userData));
  };

  const logout = () => {
    logoutMutation.mutateAsync().finally(() => {
      setUser(null);
      Sentry.setUser(null);
      Sentry.setTag("user_role", "anonymous");
      localStorage.removeItem(STORAGE_TOKENS.USER_DATA);
    });
  };

  const isAuthenticated = !!user;

  // Verifica se o usuário tem uma permissão específica
  const hasPermission = useCallback((permission: Permission): boolean => {
    if (!user) return false;
    return checkPermission(user.role, permission);
  }, [user]);

  // Verifica se o usuário tem pelo menos uma das permissões fornecidas
  const hasAnyPermission = useCallback((permissions: Permission[]): boolean => {
    if (!user) return false;
    return checkAnyPermission(user.role, permissions);
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1A1A1A] text-white">
        Carregando...
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated,
        loading,
        hasPermission,
        hasAnyPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de AuthProvider");
  }
  return context;
}